import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowUpRight } from "lucide-react";
import { useAdminAuth, formatApiError } from "./AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const { user, checking, login } = useAdminAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#5C6661]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-6">
      <div
        className="orb orb-sage"
        style={{ width: 460, height: 460, top: -120, left: -120 }}
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md rounded-3xl border border-black/5 bg-white p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
      >
        <div className="mb-8">
          <img
            src="/logo.jpg"
            alt="Neurological Communication Partners"
            className="h-20 w-auto rounded-xl ring-1 ring-black/5"
          />
        </div>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-2">
          <Lock size={14} strokeWidth={1.5} className="text-[#739072]" />
          Practice admin
        </div>
        <h1
          data-testid="admin-login-title"
          className="font-serif text-3xl font-light tracking-tight text-[#2D332F] mb-6"
        >
          Sign in to your dashboard.
        </h1>

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          data-testid="admin-login-form"
        >
          <div>
            <Label htmlFor="email" className="text-[#2D332F]">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-[#2D332F]">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-1.5"
            />
          </div>

          {error && (
            <p
              role="alert"
              data-testid="admin-login-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                Sign in <ArrowUpRight size={16} strokeWidth={1.5} />
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-xs text-[#5C6661] text-center">
          5 failed attempts trigger a 15-minute lockout.
        </p>
      </motion.div>
    </div>
  );
}
